import { NewSpeedDial, SpeedDial, UpdateSpeedDial } from '@missionlabs/types'
import { BaseQueryFn } from '@reduxjs/toolkit/dist/query'

import { customCreateApi } from './customCreateAPI'

export const buildSpeedDialApi = (baseQuery: BaseQueryFn) => {
    const api = customCreateApi({
        reducerPath: 'speedDialAPI',
        baseQuery: baseQuery,
        tagTypes: ['AdminSpeedDial', 'UserSpeedDial'],
        endpoints: builder => ({
            getSpeedDialsForClient: builder.query<SpeedDial[], void>({
                query: () => `/speeddials`,
                transformResponse: (response: { data: SpeedDial[] }) => response.data,
                providesTags: result => {
                    return result
                        ? [
                              ...result.map(({ speedDialCode }) => ({
                                  type: 'AdminSpeedDial' as const,
                                  id: speedDialCode
                              })),
                              'AdminSpeedDial'
                          ]
                        : ['AdminSpeedDial']
                }
            }),

            getSpeedDialForClient: builder.query<SpeedDial, Pick<SpeedDial, 'speedDialCode'>>({
                query: ({ speedDialCode }) => `/speeddials/${speedDialCode}`,
                providesTags: data => [{ type: 'AdminSpeedDial', id: data?.speedDialCode }]
            }),
            createSpeedDialForClient: builder.mutation<SpeedDial, NewSpeedDial>({
                query: data => ({
                    url: `/speeddials`,
                    method: 'POST',
                    body: data
                }),
                invalidatesTags: ['AdminSpeedDial']
            }),

            updateSpeedDialForClient: builder.mutation<SpeedDial, UpdateSpeedDial>({
                query: ({ oldSpeedDialCode, ...data }) => ({
                    url: `/speeddials/${oldSpeedDialCode}`,
                    method: 'PUT',
                    body: data
                }),
                invalidatesTags: (_data, _ignore, input) => [
                    { type: 'AdminSpeedDial', id: input?.oldSpeedDialCode },
                    'AdminSpeedDial'
                ]
            }),

            deleteSpeedDialForClient: builder.mutation<SpeedDial, Partial<SpeedDial>>({
                query: data => ({
                    url: `/speeddials/${data.speedDialCode}`,
                    method: 'DELETE'
                }),
                invalidatesTags: (_data, _ignore, input) => [
                    { type: 'AdminSpeedDial', id: input?.speedDialCode },
                    'AdminSpeedDial'
                ]
            }),

            getSpeedDialsForUser: builder.query<SpeedDial[], { userID: string }>({
                query: ({ userID }) => `/users/${userID}/speeddials`,
                transformResponse: (response: { data: SpeedDial[] }) => response.data,
                providesTags: result => {
                    return result
                        ? [
                              ...result.map(({ speedDialCode }) => ({
                                  type: 'UserSpeedDial' as const,
                                  id: speedDialCode
                              })),
                              'UserSpeedDial'
                          ]
                        : ['UserSpeedDial']
                }
            }),

            getSpeedDialForUser: builder.query<
                SpeedDial,
                { userID: string; speedDialCode: string }
            >({
                async queryFn({ userID, speedDialCode }, _api, _extraOptions, baseQuery) {
                    const res = await baseQuery(`/users/${userID}/speeddials/${speedDialCode}`)
                    const error = res.error as { status: number }
                    // prevent 404's rejecting promise
                    if (error && error.status === 404) return { data: null as any }
                    if (error) return { error: res.error }
                    return { data: res.data as SpeedDial }
                },
                providesTags: data => [{ type: 'UserSpeedDial', id: data?.speedDialCode }]
            }),

            createSpeedDialForUser: builder.mutation<SpeedDial, NewSpeedDial & { userID: string }>({
                query: ({ userID, ...data }) => ({
                    url: `/users/${userID}/speeddials`,
                    method: 'POST',
                    body: data
                }),
                invalidatesTags: ['UserSpeedDial']
            }),

            updateSpeedDialForUser: builder.mutation<
                SpeedDial,
                UpdateSpeedDial & { userID: string }
            >({
                query: ({ userID, oldSpeedDialCode, ...data }) => ({
                    url: `/users/${userID}/speeddials/${oldSpeedDialCode}`,
                    method: 'PUT',
                    body: data
                }),
                invalidatesTags: (_data, _ignored, input) => [
                    { type: 'UserSpeedDial', id: input?.oldSpeedDialCode },
                    'UserSpeedDial'
                ]
            }),

            deleteSpeedDialForUser: builder.mutation<
                SpeedDial,
                Pick<SpeedDial, 'speedDialCode' | 'owner'>
            >({
                query: ({ owner, speedDialCode }) => ({
                    url: `/users/${owner}/speeddials/${speedDialCode}`,
                    method: 'DELETE'
                }),
                invalidatesTags: (_data, _ignored, input) => [
                    { type: 'UserSpeedDial', id: input?.speedDialCode },
                    'UserSpeedDial'
                ]
            })
        })
    })

    return { api, ...api }
}
